import { useEffect, useRef, useState, type ReactNode } from 'react';
import { NJAutocomplete, NJFormItem, NJIconButton, NJSelect } from '@engie-group/fluid-design-system-react';
import { Size2MixStatusTag } from '@shared/size2mix-shell';
import { errorToast, successToast } from './parts';

type AutocompleteItem = { name: string; value: string };

const CONTRACTS = ['Les Tilleuls', 'Grand Lyon Est', 'Métropole Lilloise Nord', 'Saclay Campus'];

/** Nemo `Tag`: grey chip; on hover (edit only) darker background + close icon button. */
function ContractTag({ label, onDelete }: { label: string; onDelete?: () => void }) {
    const [hover, setHover] = useState(false);
    const showClose = Boolean(onDelete) && hover;
    return (
        <span
            className={`ns-contract-tag${showClose ? ' ns-contract-tag--hover' : ''}`}
            onMouseEnter={() => setHover(true)}
            onMouseLeave={() => setHover(false)}
            onFocus={() => setHover(true)}
        >
            <span className="ns-contract-tag__text">{label}</span>
            {showClose && <NJIconButton icon="close" label={`Dissocier ${label}`} variant="tertiary" scale="md" onClick={onDelete} />}
        </span>
    );
}

/** « Contrat » row as in production: tag when set, outlined « + » opening an autocomplete when empty. */
export function ContractField({ canEdit, contract, setContract }: { canEdit: boolean; contract: string; setContract: (c: string) => void }) {
    const [adding, setAdding] = useState(false);
    const [selected, setSelected] = useState<AutocompleteItem | undefined>();

    if (contract) {
        return (
            <ContractTag
                label={contract}
                onDelete={canEdit ? () => { setContract(''); successToast('Contrat dissocié avec succès'); } : undefined}
            />
        );
    }
    if (!canEdit) return <span className="ns-muted">-</span>;
    if (!adding) {
        return (
            <span className="ns-add-btn">
                <NJIconButton icon="add" label="Associer un contrat" variant="tertiary" scale="sm" onClick={() => setAdding(true)} />
            </span>
        );
    }
    const close = () => { setAdding(false); setSelected(undefined); };
    return (
        <div className="ns-inline-edit">
            <div style={{ width: 320 }}>
                <NJAutocomplete
                    id="network-contract"
                    label="Associer un contrat"
                    data={CONTRACTS.map((c) => ({ name: c, value: c }))}
                    value={selected}
                    onChange={setSelected}
                    listLabel="Suggestions — Associer un contrat"
                    inputInstructions="Utilisez les flèches haut/bas puis Entrée pour sélectionner"
                    resultsCountMessage={(n) => `${n} résultat${n > 1 ? 's' : ''}`}
                />
            </div>
            <NJIconButton
                icon="check"
                label="Valider"
                variant="tertiary"
                scale="sm"
                onClick={() => {
                    if (!selected) { errorToast('Veuillez sélectionner un contrat'); return; }
                    setContract(selected.value);
                    successToast('Contrat associé avec succès');
                    close();
                }}
            />
            <NJIconButton icon="close" label="Annuler" variant="tertiary" scale="sm" onClick={close} />
        </div>
    );
}

// ---------------------------------------------------------------------------
// Inline editing of the network information, as on integration
// (/next/networks/:id/general-alpha), ported from the `ref-network-general` reference page.
// ---------------------------------------------------------------------------

type NetworkStatus = 'Actif' | 'Archivé';
type StudyPhase = 'Développement' | 'Construction' | 'Opération' | 'Autre';

export interface NetworkInfo {
    name: string;
    description: string;
    timezone: string;
    status: NetworkStatus;
    studyPhase: StudyPhase;
}

const DESCRIPTION_MAX = 500;
const TIMEZONES = ['Europe/Paris', 'Europe/London', 'Europe/Brussels', 'Europe/Madrid', 'America/New_York', 'Asia/Singapore', 'UTC'];
const STATUSES: NetworkStatus[] = ['Actif', 'Archivé'];
const STUDY_PHASES: StudyPhase[] = ['Développement', 'Construction', 'Opération', 'Autre'];
const STUDY_PHASE_VARIANT = { Développement: 'information', Construction: 'planet', Opération: 'discovery', Autre: 'neutral' } as const;
const SELECT_HELP = 'Utilisez les flèches haut/bas puis Entrée pour sélectionner';

export const INITIAL_INFO: NetworkInfo = {
    name: 'test PUC',
    description: 'Étude de raccordement du quartier des Tilleuls au réseau de chaleur existant.',
    timezone: 'Europe/Paris',
    status: 'Actif',
    studyPhase: 'Développement',
};

/** Nemo `HoverableElement` + `EditableField`: pencil on hover, then editor + ✓/✕ (Enter submits, Escape cancels). */
function InlineEditable({ canEdit, label, display, renderEditor, onSubmit, onStart }: {
    canEdit: boolean;
    label: string;
    display: ReactNode;
    renderEditor: () => ReactNode;
    onSubmit: () => boolean;
    onStart: () => void;
}) {
    const [editing, setEditing] = useState(false);
    const [hover, setHover] = useState(false);
    const ref = useRef<HTMLDivElement>(null);
    const submit = () => onSubmit() && setEditing(false);
    const cancel = () => setEditing(false);

    useEffect(() => {
        if (!editing) return;
        const onKey = (e: KeyboardEvent) => {
            if (!ref.current?.contains(e.target as Node)) return;
            if (e.key === 'Enter' && !(e.target instanceof HTMLTextAreaElement && e.shiftKey)) submit();
            if (e.key === 'Escape') cancel();
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    });

    const value = <div className="ns-inline-value">{display || <span className="ns-muted">-</span>}</div>;
    if (!canEdit) return value;
    if (editing) {
        return (
            <div ref={ref} className="ns-inline-edit">
                {renderEditor()}
                <NJIconButton icon="check" label="Valider" variant="tertiary" scale="sm" onClick={submit} />
                <NJIconButton icon="close" label="Annuler" variant="tertiary" scale="sm" onClick={cancel} />
            </div>
        );
    }
    return (
        <div className="ns-inline-hover" onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)} onFocus={() => setHover(true)}>
            {value}
            <span style={{ visibility: hover ? 'visible' : 'hidden' }}>
                <NJIconButton icon="mode_edit" label={`Modifier ${label.toLowerCase()}`} variant="tertiary" scale="sm" onClick={() => { onStart(); setEditing(true); }} />
            </span>
        </div>
    );
}

/** Rows Nom → Phase d'étude of « Informations du réseau », editable for owner and « Peut modifier ». */
export function useInfoRows(info: NetworkInfo, onChange: (patch: Partial<NetworkInfo>) => void, canEdit: boolean) {
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [timezone, setTimezone] = useState<AutocompleteItem | undefined>();
    const [status, setStatus] = useState<NetworkStatus>('Actif');
    const [phase, setPhase] = useState<StudyPhase>('Autre');

    const save = (patch: Partial<NetworkInfo>, message: string) => {
        onChange(patch);
        successToast(message);
        return true;
    };

    return [
        {
            label: 'Nom',
            value: (
                <InlineEditable
                    canEdit={canEdit}
                    label="Nom"
                    display={info.name}
                    onStart={() => setName(info.name)}
                    renderEditor={() => (
                        <div style={{ width: 320 }}>
                            <NJFormItem id="network-name" label="Nom" size="sm" value={name} autoFocus onChange={(e) => setName((e.target as HTMLInputElement).value)} />
                        </div>
                    )}
                    onSubmit={() => {
                        if (!name.trim()) { errorToast('Le nom ne peut pas être vide'); return false; }
                        return name.trim() === info.name || save({ name: name.trim() }, 'Nom modifié avec succès');
                    }}
                />
            ),
        },
        {
            label: 'Description',
            value: (
                <InlineEditable
                    canEdit={canEdit}
                    label="Description"
                    display={info.description}
                    onStart={() => setDescription(info.description)}
                    renderEditor={() => (
                        <div style={{ width: 460 }}>
                            <NJFormItem
                                id="network-description"
                                label="Description"
                                isMultiline
                                rows={2}
                                maxLength={DESCRIPTION_MAX}
                                placeholder="Ajouter une description..."
                                value={description}
                                subscriptMessage={`${description.length}/${DESCRIPTION_MAX} caractères`}
                                onChange={(e) => setDescription((e.target as HTMLTextAreaElement).value)}
                            />
                        </div>
                    )}
                    onSubmit={() => description === info.description || save({ description }, 'Description modifiée avec succès')}
                />
            ),
        },
        {
            label: 'Fuseau horaire',
            value: (
                <InlineEditable
                    canEdit={canEdit}
                    label="Fuseau horaire"
                    display={info.timezone}
                    onStart={() => setTimezone({ name: info.timezone, value: info.timezone })}
                    renderEditor={() => (
                        <div style={{ width: 320 }}>
                            <NJAutocomplete
                                id="network-timezone"
                                label="Fuseau horaire"
                                data={TIMEZONES.map((tz) => ({ name: tz, value: tz }))}
                                value={timezone}
                                onChange={setTimezone}
                                listLabel="Fuseaux horaires"
                                inputInstructions={SELECT_HELP}
                                resultsCountMessage={(n) => `${n} résultat${n > 1 ? 's' : ''}`}
                            />
                        </div>
                    )}
                    onSubmit={() => !timezone || timezone.value === info.timezone || save({ timezone: timezone.value }, 'Fuseau horaire mis à jour avec succès')}
                />
            ),
        },
        {
            label: 'Statut',
            value: (
                <InlineEditable
                    canEdit={canEdit}
                    label="Statut"
                    display={<Size2MixStatusTag label={info.status} size="lg" />}
                    onStart={() => setStatus(info.status)}
                    renderEditor={() => (
                        <div style={{ width: 200 }}>
                            <NJSelect.Root id="network-status" label="Statut" listNavigationLabel={SELECT_HELP} buttonDefaultValueLabel="Sélectionner un statut" initialValue={info.status} onChange={(v) => v && setStatus(v as NetworkStatus)}>
                                {STATUSES.map((s) => <NJSelect.Item key={s} value={s}>{s}</NJSelect.Item>)}
                            </NJSelect.Root>
                        </div>
                    )}
                    onSubmit={() => status === info.status || save({ status }, status === 'Actif' ? 'Réseau activé avec succès' : 'Réseau archivé avec succès')}
                />
            ),
        },
        {
            label: 'Phase d’étude',
            value: (
                <InlineEditable
                    canEdit={canEdit}
                    label="Phase d’étude"
                    display={<Size2MixStatusTag label={info.studyPhase} variant={STUDY_PHASE_VARIANT[info.studyPhase]} size="lg" />}
                    onStart={() => setPhase(info.studyPhase)}
                    renderEditor={() => (
                        <div style={{ width: 220 }}>
                            <NJSelect.Root id="network-study-phase" label="Phase d’étude" listNavigationLabel={SELECT_HELP} buttonDefaultValueLabel="Sélectionner une phase" initialValue={info.studyPhase} onChange={(v) => v && setPhase(v as StudyPhase)}>
                                {STUDY_PHASES.map((p) => <NJSelect.Item key={p} value={p}>{p}</NJSelect.Item>)}
                            </NJSelect.Root>
                        </div>
                    )}
                    onSubmit={() => phase === info.studyPhase || save({ studyPhase: phase }, 'Phase d’étude modifiée avec succès')}
                />
            ),
        },
    ];
}
